import { Check } from "lucide-react";

import { BoardroomPreview } from "@/components/sections/BoardroomPreview";
import { FinalCta } from "@/components/sections/FinalCta";
import { HowItWorks } from "@/components/sections/HowItWorks";
import { PrivacyFirst } from "@/components/sections/PrivacyFirst";
import { ProcessingModes } from "@/components/sections/ProcessingModes";
import { ProductPreview } from "@/components/sections/ProductPreview";
import { Workforce } from "@/components/sections/Workforce";
import { PageHeader } from "@/components/ui/PageHeader";
import { ButtonLink } from "@/components/ui/Button";
import { ContactEmail } from "@/components/ui/ContactEmail";
import { PrimaryCta } from "@/components/ui/PrimaryCta";
import { Section, SectionHeading } from "@/components/ui/Section";
import { useLocale } from "@/i18n";

/* -------------------------------------------------------------------------- */

export function FeaturesPage() {
  const { t } = useLocale();
  return (
    <>
      <PageHeader
        title={t.pages.features.title}
        subtitle={t.pages.features.subtitle}
      />
      <ProductPreview />
      <Workforce />
      <BoardroomPreview />
      <FinalCta />
    </>
  );
}

/* -------------------------------------------------------------------------- */

export function HowItWorksPage() {
  const { t } = useLocale();
  return (
    <>
      <PageHeader title={t.pages.how.title} subtitle={t.pages.how.subtitle} />
      <HowItWorks heading={false} />
      <BoardroomPreview />
      <FinalCta />
    </>
  );
}

/* -------------------------------------------------------------------------- */

export function ProcessingModesPage() {
  const { t } = useLocale();
  return (
    <>
      <PageHeader title={t.pages.modes.title} subtitle={t.pages.modes.subtitle} />
      <ProcessingModes heading={false} />
      <PrivacyFirst />
      <FinalCta />
    </>
  );
}

/* -------------------------------------------------------------------------- */

export function SecurityPage() {
  const { t } = useLocale();
  const c = t.pages.security.commitments;
  const commitments = [c.one, c.two, c.three, c.four, c.five];

  return (
    <>
      <PageHeader
        title={t.pages.security.title}
        subtitle={t.pages.security.subtitle}
      />

      <Section>
        <SectionHeading
          title={t.pages.security.commitmentsTitle}
          align="start"
          className="mx-0"
        />
        <ul className="mt-10 grid max-w-3xl gap-4">
          {commitments.map((item) => (
            <li
              key={item}
              className="card-surface flex items-start gap-3.5 p-5 text-sm leading-relaxed text-cream-100/70"
            >
              <span
                aria-hidden
                className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-300"
              >
                <Check size={12} strokeWidth={3} />
              </span>
              {item}
            </li>
          ))}
        </ul>

        {/*
          Honest status framing. The brief requires that we never imply an
          unfinished capability already works.
        */}
        <div className="card-surface mt-12 max-w-3xl border-lavender-400/20 p-6">
          <h3 className="font-semibold text-cream-50">
            {t.pages.security.statusTitle}
          </h3>
          <p className="mt-3 text-sm leading-relaxed text-cream-100/58">
            {t.pages.security.statusNote}
          </p>
        </div>
      </Section>

      <PrivacyFirst />
      <FinalCta />
    </>
  );
}

/* -------------------------------------------------------------------------- */

export function PricingPage() {
  const { t } = useLocale();

  return (
    <>
      <PageHeader
        title={t.pages.pricing.title}
        subtitle={t.pages.pricing.subtitle}
      />

      <Section>
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-base leading-relaxed text-cream-100/62">
            {t.pages.pricing.body}
          </p>

          {/*
            Deliberately no price table and no placeholder numbers. Inventing
            tiers now would be a claim we cannot stand behind.
          */}
          <div className="card-surface mt-12 p-8 md:p-10">
            <h2 className="text-2xl font-semibold text-cream-50">
              {t.pages.pricing.earlyAccessTitle}
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-cream-100/58">
              {t.pages.pricing.earlyAccessBody}
            </p>
            <div className="mt-8">
              <PrimaryCta size="lg" className="w-full sm:w-auto" />
            </div>
          </div>
        </div>
      </Section>

      <FinalCta />
    </>
  );
}

/* -------------------------------------------------------------------------- */

export function ContactPage() {
  const { t } = useLocale();

  return (
    <>
      <PageHeader
        title={t.pages.contact.title}
        subtitle={t.pages.contact.subtitle}
      />

      <Section>
        <div className="card-surface mx-auto max-w-xl p-8 text-center md:p-10">
          <p className="text-sm tracking-wide text-cream-100/45">
            {t.pages.contact.emailLabel}
          </p>
          <div className="mt-3 text-xl font-medium md:text-2xl">
            <ContactEmail className="inline-block text-emerald-300 transition-colors hover:text-emerald-200" />
          </div>
        </div>
      </Section>
    </>
  );
}

/* -------------------------------------------------------------------------- */

export function NotFoundPage() {
  const { t, path } = useLocale();

  return (
    <Section className="text-center">
      <h1 className="text-display text-4xl font-semibold md:text-5xl">
        {t.pages.notFound.title}
      </h1>
      <p className="mt-5 text-cream-100/60">{t.pages.notFound.subtitle}</p>
      <ButtonLink to={path("/")} size="lg" className="mt-10">
        {t.cta.backHome}
      </ButtonLink>
    </Section>
  );
}
